import React, { createContext, useState, useContext, useCallback } from 'react';
import { quotesAPI } from '../services/api';

const QuotesContext = createContext(null);

export const QuotesProvider = ({ children }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchQuotes = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await quotesAPI.getAll(filters);
      setQuotes(data);
      return data;
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const addQuote = useCallback(async (quoteData) => {
    try {
      const newQuote = await quotesAPI.create(quoteData);
      setQuotes(prev => [newQuote, ...prev]);
      return { success: true, data: newQuote };
    } catch (err) {
      console.error('Failed to create quote:', err);
      return { success: false, message: err.message };
    }
  }, []);

  const updateQuoteStatus = useCallback(async (quoteId, newStatus) => {
    try {
      const updatedQuote = await quotesAPI.updateStatus(quoteId, newStatus);
      setQuotes(prev =>
        prev.map(quote => quote.id === quoteId ? updatedQuote : quote)
      );
      return { success: true, data: updatedQuote };
    } catch (err) {
      console.error('Failed to update quote status:', err);
      return { success: false, message: err.message };
    }
  }, []);

  const deleteQuote = useCallback(async (quoteId) => {
    try {
      await quotesAPI.delete(quoteId);
      setQuotes(prev => prev.filter(quote => quote.id !== quoteId));
      return { success: true };
    } catch (err) {
      console.error('Failed to delete quote:', err);
      return { success: false, message: err.message };
    }
  }, []);

  return (
    <QuotesContext.Provider value={{ 
      quotes, 
      loading, 
      error, 
      fetchQuotes, 
      addQuote, 
      updateQuoteStatus, 
      deleteQuote 
    }}>
      {children}
    </QuotesContext.Provider>
  );
};

export const useQuotes = () => {
  const context = useContext(QuotesContext);
  if (!context) {
    throw new Error('useQuotes must be used within a QuotesProvider');
  }
  return context;
};
